import * as ProgressPrimitive from '@radix-ui/react-progress';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Progress — straight bar variant.
 *
 * Built on Radix Progress. The "stacked" multi-segment variant
 * we used for the Storage card uses the lower-level
 * `ProgressSegmented` below so we get full control over each
 * segment's color and tooltip wiring.
 */

interface ProgressProps
  extends ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> {
  // 0–100. Caller-provided. Out-of-range values clamp.
  value?: number | undefined;
  // Mantine-style color name. Maps to CSS variable + Tailwind classes.
  tone?: 'primary' | 'green' | 'yellow' | 'red' | 'neutral';
}

const TONE_BG: Record<NonNullable<ProgressProps['tone']>, string> = {
  primary: 'bg-theia-500',
  green: 'bg-green-500',
  yellow: 'bg-yellow-500',
  red: 'bg-red-500',
  neutral: 'bg-neutral-500',
};

export const Progress = forwardRef<
  ElementRef<typeof ProgressPrimitive.Root>,
  ProgressProps
>(({ className, value, tone = 'primary', ...props }, ref) => {
  const clamped = Math.max(0, Math.min(100, value ?? 0));
  return (
    <ProgressPrimitive.Root
      ref={ref}
      className={cn(
        'relative h-1.5 w-full overflow-hidden rounded-full bg-neutral-800',
        className,
      )}
      {...props}
    >
      <ProgressPrimitive.Indicator
        className={cn('h-full transition-all', TONE_BG[tone])}
        style={{ width: `${clamped}%` }}
      />
    </ProgressPrimitive.Root>
  );
});
Progress.displayName = 'Progress';

/**
 * Segmented progress bar — multiple color stops along one track.
 * Used by the Storage card to show "Perforce share / other / free"
 * stacked on a single bar. Each segment can carry its own tooltip
 * via the `label` prop on a `ProgressSegment`.
 */
interface ProgressSegment {
  value: number;
  // Either a tone keyword OR an arbitrary Tailwind bg-class for
  // one-off colors not in the standard palette.
  tone?: NonNullable<ProgressProps['tone']>;
  className?: string;
  // Wrap the segment in a tooltip if provided. The wrapper element
  // is handed off by the caller as JSX.
  wrap?: (segment: ReactNode) => ReactNode;
}

export function ProgressSegmented({
  segments,
  className,
}: {
  segments: ProgressSegment[];
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex h-1.5 w-full overflow-hidden rounded-full bg-neutral-800',
        className,
      )}
    >
      {segments.map((seg, idx) => {
        const node = (
          <div
            key={idx}
            className={cn(
              'h-full transition-all',
              seg.tone ? TONE_BG[seg.tone] : seg.className,
            )}
            style={{ width: `${Math.max(0, Math.min(100, seg.value))}%` }}
          />
        );
        return seg.wrap ? <div key={idx}>{seg.wrap(node)}</div> : node;
      })}
    </div>
  );
}
